import { ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { PartnerEffects } from "../../State/PartnerEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { Partner } from "../../Dto/PartnerModel";
import { PartnerType } from "../../Dto/PartnerModel";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-partner-edit",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, LocationSelectorComponent, AutocompleteComponent],
  templateUrl: "./PartnerEditPage.html",
  styleUrl: "./PartnerEditPage.scss",
})
export class PartnerEditPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly partnerEffects = inject(PartnerEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly partnerId = this.route.snapshot.paramMap.get("id") ?? undefined;

  typeOptions = Object.values(PartnerType).map((type) => ({
    value: type,
    label: type,
  }));

  partnerForm: FormGroup;
  partner: Partner | null = null;
  isReady = false;
  isSaving = false;

  constructor() {
    this.partnerForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      type: ["", [Validators.required]],
      address: this.fb.group({
        stateProvinceRegion: ["", [Validators.required]],
        countyDistrict: ["", [Validators.required]],
        subdistrictMunicipality: ["", [Validators.required]],
        communeVillage: ["", [Validators.required]],
        line: [""],
        zip: [""],
        code: [""],
        longitude: [null],
        latitude: [null],
      }),
    });
  }

  ngOnInit(): void {
    if (!this.partnerId) {
      this.router.navigate(["/partners"]).catch(() => undefined);
      return;
    }

    this.fetchPartner();
  }

  private async fetchPartner(): Promise<void> {
    if (!this.partnerId) {
      this.toastEffects.show("Partner ID is required.", "error");
      await this.router.navigate(["/partners"]);
      return;
    }

    const partner = await this.partnerEffects.getPartnerById(this.partnerId);
    if (!partner) {
      this.toastEffects.show("Partner not found.", "error");
      await this.router.navigate(["/partners"]);
      return;
    }

    setTimeout(() => {
      this.partner = partner;
      this.patchFormFromPartner(partner);
      this.isReady = true;
      this.cdr.detectChanges();
    }, 0);
  }

  async savePartner(): Promise<void> {
    if (this.partnerForm.invalid) {
      this.partnerForm.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    if (!this.partnerId) {
      this.toastEffects.show("Missing partner identifier.", "error");
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();

    const formData = this.partnerForm.value;
    const updateData = {
      name: formData.name,
      type: formData.type,
      address: {
        stateProvinceRegion: formData.address.stateProvinceRegion,
        countyDistrict: formData.address.countyDistrict,
        subdistrictMunicipality: formData.address.subdistrictMunicipality,
        communeVillage: formData.address.communeVillage,
        line: formData.address.line || "",
        zip: formData.address.zip || "",
        code: formData.address.code || "",
        longitude: formData.address.longitude ?? null,
        latitude: formData.address.latitude ?? null,
      },
    };

    try {
      const updatedPartner = await this.partnerEffects.updatePartner(this.partnerId, updateData);

      setTimeout(() => {
        this.partner = updatedPartner;
        this.patchFormFromPartner(updatedPartner);
        this.toastEffects.show("Partner updated successfully.", "success");
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update partner."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  goBack(): void {
    this.router.navigate(["/partners"]).catch(() => undefined);
  }

  onTypeChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }
    this.partnerForm.patchValue({ type: value });
  }

  onAddressChange(value: LocationAddress): void {
    this.partnerForm.patchValue({
      address: {
        stateProvinceRegion: value.stateProvinceRegion,
        countyDistrict: value.countyDistrict,
        subdistrictMunicipality: value.subdistrictMunicipality,
        communeVillage: value.communeVillage,
        line: value.line,
        zip: value.zip,
        code: value.code,
        longitude: value.longitude,
        latitude: value.latitude,
      },
    });
  }

  private patchFormFromPartner(partner: Partner): void {
    if (this.partnerForm.dirty) {
      return;
    }

    this.partnerForm.patchValue(
      {
        name: partner.name,
        type: partner.type,
        address: {
          stateProvinceRegion: partner.address.stateProvinceRegion,
          countyDistrict: partner.address.countyDistrict,
          subdistrictMunicipality: partner.address.subdistrictMunicipality,
          communeVillage: partner.address.communeVillage,
          line: partner.address.line || "",
          zip: partner.address.zip || "",
          code: partner.address.code || "",
          longitude: partner.address.longitude ?? null,
          latitude: partner.address.latitude ?? null,
        },
      },
      { emitEvent: false },
    );

    const typeValue = this.partnerForm.get("type")?.value;
    if (typeValue) {
      this.onTypeChange(typeValue);
    }
  }
}
